import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail, Lock } from "lucide-react";

import AuthShell from "@/components/auth/AuthShell";
import Modal from "@/components/Modal";
import {
  AUTH_CARD_CLASS,
  AUTH_FOOTER_LINK_CLASS,
  AUTH_LABEL_CLASS,
  AUTH_PRIMARY_BUTTON_CLASS,
  authInputClass,
} from "@/components/auth/authStyles";
import {
  requestPasswordResetCodeAPI,
  resetPasswordAPI,
  verifyPasswordResetCodeAPI,
} from "@/services/api/user/passwordReset";

type Step = "email" | "code" | "password";

interface ForgotPasswordPageProps {
  mode?: "student" | "business";
}

const EMAIL_STATUS = {
  CODE_EXPIRED: -20019,
  CODE_INVALID: -20020,
  TOO_MANY_ATTEMPTS: -20021,
  CODE_ALREADY_USED: -20022,
};

const ForgotPasswordPage: React.FC<ForgotPasswordPageProps> = ({
  mode = "student",
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [seconds, setSeconds] = useState(0);
  const [busy, setBusy] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalText, setModalText] = useState("");
  const [resetComplete, setResetComplete] = useState(false);

  const loginPath = mode === "business" ? "/business/login" : "/login";

  useEffect(() => {
    if (seconds <= 0) return undefined;
    const timer = setInterval(() => {
      setSeconds((value) => (value > 0 ? value - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [seconds]);

  const showAlert = (text: string) => {
    setModalText(t(text));
    setModalVisible(true);
  };

  const closeAlert = () => {
    setModalVisible(false);
    if (resetComplete) navigate(loginPath);
  };

  const requestCode = async () => {
    if (busy) return;
    if (!email.trim()) {
      return showAlert("Please fill in all the blank");
    }
    setBusy(true);
    const resp = await requestPasswordResetCodeAPI(email, mode);
    setBusy(false);
    if (resp?.status === 0) {
      setStep("code");
      setSeconds(60);
      return;
    }
    showAlert("Network error, please try again");
  };

  const verifyCode = async () => {
    if (busy) return;
    if (!code.trim()) {
      return showAlert("signup.enterEmailCode");
    }
    setBusy(true);
    const resp = await verifyPasswordResetCodeAPI(email, code, mode);
    setBusy(false);
    if (resp?.status === 0 && resp?.result?.verified) {
      setStep("password");
      return;
    }
    if (resp?.status === EMAIL_STATUS.CODE_EXPIRED) {
      return showAlert("Verification code expired, please resend");
    }
    if (resp?.status === EMAIL_STATUS.CODE_INVALID) {
      return showAlert("Invalid verification code");
    }
    if (resp?.status === EMAIL_STATUS.TOO_MANY_ATTEMPTS) {
      return showAlert("Too many attempts, please resend");
    }
    if (resp?.status === EMAIL_STATUS.CODE_ALREADY_USED) {
      return showAlert("Verification code already used, please resend");
    }
    showAlert("Network error, please try again");
  };

  const resetPassword = async () => {
    if (busy) return;
    if (!password || !confirmPassword) {
      return showAlert("Please fill in all the blank");
    }
    if (password.length < 8) {
      return showAlert("Password must be at least 8 digits!");
    }
    if (password !== confirmPassword) {
      return showAlert("Password and Confirmed Password are not match");
    }
    setBusy(true);
    const resp = await resetPasswordAPI(email, password, mode);
    setBusy(false);
    if (resp?.status === 0) {
      setResetComplete(true);
      setModalText(t("Password reset successfully"));
      setModalVisible(true);
      return;
    }
    if (resp?.status === EMAIL_STATUS.CODE_EXPIRED) {
      setStep("email");
      return showAlert("Verification code expired, please resend");
    }
    showAlert("Network error, please try again");
  };

  return (
    <>
      <AuthShell contentClassName="max-w-md">
        <div className={AUTH_CARD_CLASS} aria-busy={busy}>
          <h1 className="text-2xl font-bold text-[#333]">
            {t("Forgot Password")}
          </h1>
          <p className="mt-1.5 text-sm text-[#333]/45">
            {step === "email" &&
              t(
                "Enter the email address linked to your account and we will send you a verification code.",
              )}
            {step === "code" && t("Enter the verification code")}
            {step === "password" &&
              t("Choose a password with at least 8 characters.")}
          </p>

          <div className="mt-6 space-y-4">
            {step === "email" && (
              <>
                <div>
                  <label className={AUTH_LABEL_CLASS}>
                    {mode === "business" ? t("Business Email") : t("Your University Email")}
                  </label>
                  <div className="relative">
                    <Mail
                      size={18}
                      className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && requestCode()}
                      disabled={busy}
                      className={authInputClass()}
                      placeholder={
                        mode === "business"
                          ? t("Business Email")
                          : t("Your University Email")
                      }
                      autoFocus
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={requestCode}
                  disabled={busy}
                  className={AUTH_PRIMARY_BUTTON_CLASS}
                >
                  {t("SEND VERIFICATION CODE")}
                </button>
              </>
            )}

            {step === "code" && (
              <>
                <p className="text-sm text-gray-500">
                  {t("Enter the verification code")}
                  <br />
                  <span className="font-semibold text-[#333]">{email}</span>
                </p>
                <div>
                  <label className={AUTH_LABEL_CLASS}>
                    {t("Verification Code")}
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && verifyCode()}
                    disabled={busy}
                    className={authInputClass()}
                    placeholder="000000"
                  />
                </div>
                {seconds > 0 ? (
                  <div className="py-2 text-center text-sm text-gray-500">
                    {seconds} {t("s")}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={requestCode}
                    disabled={busy}
                    className="w-full rounded-xl border border-red-400 py-2.5 text-sm font-bold text-red-500 transition hover:bg-red-50 disabled:opacity-50"
                  >
                    {t("RESEND VERIFICATION CODE")}
                  </button>
                )}
                <button
                  type="button"
                  onClick={verifyCode}
                  disabled={busy}
                  className={AUTH_PRIMARY_BUTTON_CLASS}
                >
                  {t("VERIFY CODE")}
                </button>
                <button
                  type="button"
                  onClick={() => setStep("email")}
                  disabled={busy}
                  className="w-full text-sm font-semibold text-[#ffa217] hover:text-[#e08f0e] disabled:opacity-50"
                >
                  {t("BACK")}
                </button>
              </>
            )}

            {step === "password" && (
              <>
                <div>
                  <label className={AUTH_LABEL_CLASS}>{t("New Password")}</label>
                  <div className="relative">
                    <Lock
                      size={18}
                      className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={authInputClass()}
                      placeholder="••••••••"
                      disabled={busy}
                      autoFocus
                    />
                  </div>
                </div>
                <div>
                  <label className={AUTH_LABEL_CLASS}>
                    {t("Confirm New Password")}
                  </label>
                  <div className="relative">
                    <Lock
                      size={18}
                      className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && resetPassword()}
                      disabled={busy}
                      className={authInputClass()}
                      placeholder="••••••••"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={resetPassword}
                  disabled={busy}
                  className={AUTH_PRIMARY_BUTTON_CLASS}
                >
                  {t("RESET PASSWORD")}
                </button>
              </>
            )}
          </div>

          <div className="mt-6 text-center text-sm text-gray-500">
            <Link
              to={loginPath}
              className={AUTH_FOOTER_LINK_CLASS}
              aria-disabled={busy}
              tabIndex={busy ? -1 : undefined}
              onClick={(e) => busy && e.preventDefault()}
            >
              {t("Back to login")}
            </Link>
          </div>
        </div>
      </AuthShell>

      {busy && (
        <div
          className="fixed inset-0 z-[9999] flex cursor-wait items-center justify-center bg-white/35 backdrop-blur-[1px]"
          aria-hidden="true"
        >
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#ffa217]/30 border-t-[#ffa217]" />
        </div>
      )}

      <Modal visible={modalVisible} onClose={closeAlert}>
        <div className="rounded-2xl bg-white p-6 text-center shadow-lg">
          <div className="mb-4 text-lg font-semibold text-[#333]">
            {modalText}
          </div>
          <button
            type="button"
            onClick={closeAlert}
            className="rounded-lg bg-[#ffa217] px-6 py-2.5 font-semibold text-white transition hover:opacity-90"
          >
            {t("OK")}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default ForgotPasswordPage;
